<template>
  <div class="coordination-page">
    <!-- 顶部背景 -->
    <div class="header-bg">
      <div class="header-content">
        <div class="page-title">协调记录</div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-card">
      <div class="stats-grid">
        <div class="stat-item pending" @click="handleStatClick('pending')">
          <div class="stat-value">{{ overviewData.pendingCount }}</div>
          <div class="stat-label">待处理</div>
        </div>
        <div class="stat-item success" @click="handleStatClick('success')">
          <div class="stat-value">{{ overviewData.successCount }}</div>
          <div class="stat-label">协调成功</div>
        </div>
        <div class="stat-item cancelled" @click="handleStatClick('cancelled')">
          <div class="stat-value">{{ overviewData.cancelledCount }}</div>
          <div class="stat-label">已取消</div>
        </div>
      </div>
    </div>

    <!-- 筛选栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <div class="date-picker" @click="showDateFilter = true">
          <van-icon name="calendar-o" />
          <span>{{ currentDateRangeDisplay }}</span>
          <van-icon name="arrow-down" />
        </div>
      </div>
      <div class="filter-right">
        <div
          class="status-filter"
          :class="{ active: selectedStatus === 'all' }"
          @click="handleStatusFilter('all')"
        >
          全部
        </div>
        <div
          class="status-filter"
          :class="{ active: selectedStatus === 'pending' }"
          @click="handleStatusFilter('pending')"
        >
          待处理
        </div>
        <div
          class="status-filter"
          :class="{ active: selectedStatus === 'success' }"
          @click="handleStatusFilter('success')"
        >
          成功
        </div>
      </div>
    </div>

    <!-- 记录列表 -->
    <div class="record-list">
      <div v-if="loading" class="loading-state">
        <van-loading type="spinner" size="24px">加载中...</van-loading>
      </div>
      <template v-else-if="recordList.length > 0">
        <div
          v-for="(item, index) in recordList"
          :key="index"
          class="record-item"
          @click="handleRecordClick(item)"
        >
          <div class="record-header">
            <div class="record-date">{{ item.createTime }}</div>
            <div class="record-status" :class="'status-' + item.status">
              {{ getStatusText(item.status) }}
            </div>
          </div>
          <div class="record-body">
            <div class="record-info-row">
              <span class="info-label">学员姓名：</span>
              <span class="info-value">{{ item.studentName }}</span>
            </div>
            <div class="record-info-row">
              <span class="info-label">原课程：</span>
              <span class="info-value">{{ item.originalCourse }}</span>
            </div>
            <div class="record-info-row">
              <span class="info-label">协调课程：</span>
              <span class="info-value highlight">{{ item.targetCourse }}</span>
            </div>
            <div class="record-info-row">
              <span class="info-label">协调原因：</span>
              <span class="info-value">{{ item.reason || '暂无' }}</span>
            </div>
          </div>
          <div class="record-footer">
            <div class="operator-info">
              <van-icon name="manager-o" />
              <span>{{ item.operatorName }}</span>
            </div>
            <van-icon name="arrow" class="arrow-icon" />
          </div>
        </div>
      </template>
      <EmptyData v-else tips="暂无协调记录" />
    </div>

    <!-- 加载更多 -->
    <div v-if="recordList.length > 0 && hasMore" class="load-more">
      <van-loading v-if="loadingMore" type="spinner" size="16px" />
      <span v-else @click="loadMore">加载更多</span>
    </div>

    <!-- 日期筛选面板 -->
    <van-popup
      v-model:show="showDateFilter"
      closeable
      position="bottom"
      round
    >
      <DateFilterPanel
        :config="{ quickType: currentQuickType }"
        @submit="handleDateFilterSubmit"
        @reset="handleDateFilterReset"
      />
    </van-popup>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue'
import { useRouter } from 'vue-router'
import EmptyData from '@/components/EmptyData.vue'
import DateFilterPanel from '@/components/dateFilterPanel/DateFilterPanel.vue'
import { queryCoordinationRecords } from '@/api/user'

const router = useRouter()

// 状态
const loading = ref(false)
const loadingMore = ref(false)
const hasMore = ref(false)
const pageNum = ref(1)
const pageSize = ref(10)
const selectedStatus = ref('all')
const showDateFilter = ref(false)
const currentQuickType = ref('thisMonth')
const dateRange = ref([])

// 概览数据
const overviewData = reactive({
  pendingCount: 0,
  successCount: 0,
  cancelledCount: 0
})

// 记录列表
const recordList = ref([])

// 计算属性
const currentDateRangeDisplay = computed(() => {
  if (dateRange.value.length === 2) {
    return `${dateRange.value[0]} ~ ${dateRange.value[1]}`
  }
  return '本月'
})

// 获取状态文本
const getStatusText = (status) => {
  const statusMap = {
    pending: '待处理',
    success: '协调成功',
    cancelled: '已取消'
  }
  return statusMap[status] || status
}

// iframe消息处理：接收web端导出参数请求
const handleIframeMessage = (event) => {
  const { data } = event
  if (data && data.type === 'getExportParams') {
    // 获取当前选中的参数（协调记录页面只有时间范围，没有机构选择）
    const orgId = '' // 协调记录页面没有机构筛选
    const startDate = dateRange.value?.[0] || ''
    const endDate = dateRange.value?.[1] || ''

    // 向web端返回参数
    if (event.source && event.source.postMessage) {
      event.source.postMessage(
        {
          type: 'exportParams',
          orgId,
          startDate,
          endDate
        },
        '*'
      )
    }
  }
}

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    const params = {
      pageNum: pageNum.value,
      pageSize: pageSize.value,
      status: selectedStatus.value === 'all' ? '' : selectedStatus.value,
      startDate: dateRange.value[0] || '',
      endDate: dateRange.value[1] || ''
    }
    const res = await queryCoordinationRecords(params)
    if (res.code === '00' && res.data) {
      // 更新概览数据
      overviewData.pendingCount = res.data.pendingCount || 0
      overviewData.successCount = res.data.successCount || 0
      overviewData.cancelledCount = res.data.cancelledCount || 0

      // 更新列表
      if (pageNum.value === 1) {
        recordList.value = res.data.list || []
      } else {
        recordList.value = [...recordList.value, ...(res.data.list || [])]
      }

      // 判断是否还有更多
      hasMore.value = res.data.list?.length === pageSize.value
    } else {
      // 模拟数据（接口未完成时使用）
      handleMockData()
    }
  } catch (error) {
    console.error('加载数据失败', error)
    handleMockData()
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// 模拟数据
const handleMockData = () => {
  overviewData.pendingCount = 5
  overviewData.successCount = 12
  overviewData.cancelledCount = 3

  const mockList = [
    {
      createTime: '2026-04-14 10:30',
      status: 'pending',
      studentName: '张同学',
      originalCourse: '数学特训班',
      targetCourse: '物理基础班',
      reason: '时间冲突',
      operatorName: '李老师'
    },
    {
      createTime: '2026-04-13 15:20',
      status: 'success',
      studentName: '王同学',
      originalCourse: '英语口语',
      targetCourse: '英语写作',
      reason: '课程调整',
      operatorName: '赵老师'
    },
    {
      createTime: '2026-04-12 09:15',
      status: 'cancelled',
      studentName: '刘同学',
      originalCourse: '化学实验',
      targetCourse: '化学理论',
      reason: '学生意愿',
      operatorName: '周老师'
    },
    {
      createTime: '2026-04-11 14:45',
      status: 'pending',
      studentName: '陈同学',
      originalCourse: '生物竞赛',
      targetCourse: '生物基础',
      reason: '难度过高',
      operatorName: '吴老师'
    }
  ]

  if (pageNum.value === 1) {
    recordList.value = mockList
  }
  hasMore.value = false
}

// 加载更多
const loadMore = () => {
  if (loadingMore.value || !hasMore.value) return
  loadingMore.value = true
  pageNum.value++
  loadData()
}

// 状态筛选
const handleStatusFilter = (status) => {
  selectedStatus.value = status
  pageNum.value = 1
  loadData()
}

// 统计卡片点击
const handleStatClick = (type) => {
  selectedStatus.value = type
  pageNum.value = 1
  loadData()
}

// 记录点击
const handleRecordClick = (item) => {
  router.push({
    path: '/coordinationDetail',
    query: { id: item.id }
  })
}

// 日期筛选提交
const handleDateFilterSubmit = (result) => {
  currentQuickType.value = result.quickType
  dateRange.value = result.dateRange
  showDateFilter.value = false
  pageNum.value = 1
  loadData()
}

// 日期筛选重置
const handleDateFilterReset = () => {
  currentQuickType.value = 'thisMonth'
  dateRange.value = []
  showDateFilter.value = false
  pageNum.value = 1
  loadData()
}

onMounted(() => {
  loadData()
  // 添加iframe消息监听
  window.addEventListener('message', handleIframeMessage)
})

onUnmounted(() => {
  window.removeEventListener('message', handleIframeMessage)
})
</script>

<style scoped lang="scss">
.coordination-page {
  min-height: 100vh;
  background: #f5f6fa;
  padding-bottom: 20px;
}

.header-bg {
  background: linear-gradient(135deg, #3478f6 0%, #5a9eff 100%);
  padding: 20px 16px 40px;
  border-radius: 0 0 24px 24px;
}

.page-title {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
}

.stats-card {
  margin: -30px 16px 12px;
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(52, 120, 246, 0.12);
}

.stats-grid {
  display: flex;
  justify-content: space-between;
}

.stat-item {
  flex: 1;
  text-align: center;
  padding: 12px 8px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:active {
    transform: scale(0.96);
  }

  &.pending {
    background: #fff7e6;
    .stat-value { color: #fa8c16; }
  }

  &.success {
    background: #e6f7ff;
    .stat-value { color: #1890ff; }
  }

  &.cancelled {
    background: #f5f5f5;
    .stat-value { color: #999; }
  }
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #3478f6;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 13px;
  color: #666;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  margin-bottom: 12px;
}

.filter-left {
  .date-picker {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #fff;
    border-radius: 20px;
    font-size: 14px;
    color: #333;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .van-icon {
      color: #3478f6;
    }
  }
}

.filter-right {
  display: flex;
  gap: 8px;
}

.status-filter {
  padding: 6px 14px;
  background: #fff;
  border-radius: 16px;
  font-size: 13px;
  color: #666;
  transition: all 0.2s;

  &.active {
    background: #3478f6;
    color: #fff;
  }

  &:active {
    opacity: 0.8;
  }
}

.record-list {
  padding: 0 16px;
}

.loading-state {
  padding: 60px 0;
  text-align: center;
}

.record-item {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.record-date {
  font-size: 13px;
  color: #999;
}

.record-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  &.status-pending {
    background: #fff7e6;
    color: #fa8c16;
  }

  &.status-success {
    background: #e6f7ff;
    color: #1890ff;
  }

  &.status-cancelled {
    background: #f5f5f5;
    color: #999;
  }
}

.record-body {
  margin-bottom: 12px;
}

.record-info-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }
}

.info-label {
  color: #999;
  min-width: 70px;
}

.info-value {
  color: #333;
  flex: 1;

  &.highlight {
    color: #3478f6;
    font-weight: 500;
  }
}

.record-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.operator-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #999;

  .van-icon {
    font-size: 16px;
  }
}

.arrow-icon {
  color: #ccc;
  font-size: 14px;
}

.load-more {
  text-align: center;
  padding: 16px;
  color: #3478f6;
  font-size: 14px;

  &:active {
    opacity: 0.7;
  }
}
</style>
